import React, { useEffect, useState, useRef } from 'react';
import api from '../services/api';
import '../styles/Profile.css';

// ── Utilities ─────────────────────────────────────────────────────────────────
const debounce = (func, delay) => {
    let id;
    return (...args) => { clearTimeout(id); id = setTimeout(() => func.apply(null, args), delay); };
};

const truncate = (str, n) => str && str.length > n ? str.slice(0, n) + '…' : (str || '');

// ── StarRating ────────────────────────────────────────────────────────────────
const StarRating = ({ value, onChange, disabled }) => (
    <div
        className="star-rating"
        role={onChange ? 'radiogroup' : 'img'}
        aria-label={`${value} out of 5 stars`}
    >
        {[1, 2, 3, 4, 5].map(star => (
            <button
                key={star}
                type="button"
                className={`star-btn${value >= star ? ' star-btn--filled' : ''}`}
                onClick={() => onChange?.(star)}
                disabled={disabled || !onChange}
                aria-label={`${star} star`}
            >
                ★
            </button>
        ))}
    </div>
);

// ── StatCard ──────────────────────────────────────────────────────────────────
const StatCard = ({ label, value }) => (
    <div className="stat-card">
        <span className="stat-value">{value ?? '—'}</span>
        <span className="stat-label">{label}</span>
    </div>
);

// ── InteractionCompletionManager ──────────────────────────────────────────────
// Handles the requester-side flow (rating the responder after completion).
// Kept in the Transactions tab for interactions received on the user's own posts.
const InteractionCompletionManager = ({
    interaction, postId, isAuthor, isResponder, onComplete,
}) => {
    const [rating,     setRating]     = useState(5);
    const [submitting, setSubmitting] = useState(false);
    const [message,    setMessage]    = useState('');

    if (!isAuthor && !isResponder) return null;

    if (interaction.is_completed) {
        return (
            <div className="icm-completed">
                🎉 Transaction completed
                {interaction.responder_rating != null && (
                    <span className="icm-rating"> · ★ {interaction.responder_rating}/5</span>
                )}
            </div>
        );
    }

    const userRole               = isAuthor ? 'requester' : 'responder';
    const isConfirmedByUser      = isAuthor
        ? interaction.requester_confirmed
        : interaction.responder_confirmed;
    const awaitingFrom           = isAuthor
        ? (interaction.responder_confirmed ? null : (interaction.responderName || 'the responder'))
        : (interaction.requester_confirmed ? null : 'the requester');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (userRole === 'requester' && (rating < 1 || rating > 5)) {
            setMessage('A rating of 1–5 is required.');
            return;
        }
        setSubmitting(true);
        setMessage('');
        try {
            const res = await api.put(`/posts/${postId}/complete`, {
                interactionId: interaction._id,
                role: userRole,
                rating,
            });
            onComplete(interaction._id, res.data);
            setMessage(res.data.message || 'Done!');
        } catch (err) {
            setMessage(err.response?.data?.message || 'Failed to complete transaction.');
        } finally {
            setSubmitting(false);
        }
    };

    if (isConfirmedByUser) {
        return (
            <div className="icm-awaiting">
                Awaiting confirmation from {awaitingFrom}.
            </div>
        );
    }

    return (
        <form className="icm-form" onSubmit={handleSubmit}>
            <p className="icm-title">Action required</p>
            {userRole === 'requester' ? (
                <div className="icm-rating-row">
                    <span className="icm-label">
                        Rate <strong>{interaction.responderName || 'responder'}</strong>
                    </span>
                    <StarRating value={rating} onChange={setRating} disabled={submitting} />
                </div>
            ) : (
                <p className="icm-label">
                    Confirm transaction completed with{' '}
                    <strong>{interaction.requesterName || 'requester'}</strong>.
                </p>
            )}
            <button type="submit" className="icm-submit-btn" disabled={submitting}>
                {submitting
                    ? 'Confirming…'
                    : userRole === 'requester' ? 'Mark Complete & Rate' : 'Confirm Transaction'}
            </button>
            {message && (
                <span className={`icm-message${message.includes('Failed') ? ' icm-message--error' : ' icm-message--ok'}`}>
                    {message}
                </span>
            )}
        </form>
    );
};

// ── ShopkeeperStatusToggle ────────────────────────────────────────────────────
const ShopkeeperStatusToggle = ({ isShopkeeper, onStatusChange }) => {
    const [toggling,    setToggling]    = useState(false);
    const [toggleError, setToggleError] = useState('');

    const handleToggle = async () => {
        setToggling(true);
        setToggleError('');
        try {
            await api.put('/users/profile/toggle-shopkeeper', { is_shopkeeper: !isShopkeeper });
            onStatusChange(!isShopkeeper);
        } catch {
            setToggleError('Failed to update status — please try again.');
        } finally {
            setToggling(false);
        }
    };

    return (
        <div className="shopkeeper-toggle">
            <p className="shopkeeper-status">
                Status:{' '}
                <strong>{isShopkeeper ? '✅ Shopkeeper / Seller' : '👤 Regular User'}</strong>
            </p>
            <button className="toggle-btn" onClick={handleToggle} disabled={toggling}>
                {toggling
                    ? 'Updating…'
                    : isShopkeeper ? 'Switch to Regular User' : 'Become a Shopkeeper'}
            </button>
            {toggleError && <span className="toggle-error">{toggleError}</span>}
        </div>
    );
};

// ── SellerCategoriesEditor ────────────────────────────────────────────────────
const SellerCategoriesEditor = ({ initialCategories, onUpdate }) => {
    const [descriptionInput, setDescriptionInput] = useState('');
    const [finalCategories,  setFinalCategories]  = useState(initialCategories);
    const [isSaving,         setIsSaving]         = useState(false);
    const [isSuggesting,     setIsSuggesting]     = useState(false);
    const [message,          setMessage]          = useState('');
    const [suggestions,      setSuggestions]      = useState([]);

    const fetchSuggestions = async (text) => {
        if (text.trim().length < 5) { setSuggestions([]); return; }
        setIsSuggesting(true);
        try {
            const res = await api.post('/users/profile/suggest-categories', { text });
            setSuggestions(res.data.suggestions.filter(s => !finalCategories.includes(s)));
        } catch {
            setSuggestions([]);
        } finally {
            setIsSuggesting(false);
        }
    };

    const debouncedFetch = useRef(debounce((text) => fetchSuggestions(text), 500)).current;

    const handleInputChange = (e) => {
        setDescriptionInput(e.target.value);
        debouncedFetch(e.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setMessage('');
        try {
            const res = await api.put('/users/profile/seller-categories', {
                seller_categories: finalCategories,
            });
            setMessage('Categories updated! Your feed will now be prioritised.');
            onUpdate(res.data.seller_categories);
            setSuggestions([]);
        } catch {
            setMessage('Failed to update categories — please try again.');
        } finally {
            setIsSaving(false);
            setTimeout(() => setMessage(''), 5000);
        }
    };

    return (
        <div className="seller-editor">
            <form onSubmit={handleSubmit}>
                <label htmlFor="cat-desc" className="seller-editor-label">
                    <strong>What do you sell?</strong>{' '}
                    Type a description to get category suggestions.
                </label>
                <textarea
                    id="cat-desc"
                    className="seller-editor-textarea"
                    value={descriptionInput}
                    onChange={handleInputChange}
                    placeholder="E.g., We stock all types of ladders and power tools."
                    rows={2}
                />
                {isSuggesting && <p className="seller-hint">Analysing text…</p>}
                {suggestions.length > 0 && (
                    <div className="seller-suggestions">
                        <span className="seller-hint">Click to add:</span>
                        {suggestions.map(s => (
                            <button
                                key={s}
                                type="button"
                                className="suggestion-btn"
                                onClick={() => {
                                    setFinalCategories(prev => [...prev, s]);
                                    setSuggestions(prev => prev.filter(x => x !== s));
                                    setDescriptionInput('');
                                }}
                            >
                                + {s}
                            </button>
                        ))}
                    </div>
                )}
                <div className="seller-tags-box">
                    {finalCategories.length === 0
                        ? <span className="seller-hint">No specialties selected.</span>
                        : finalCategories.map(c => (
                            <button
                                key={c}
                                type="button"
                                className="category-tag"
                                onClick={() => setFinalCategories(prev => prev.filter(x => x !== c))}
                            >
                                {c} ✕
                            </button>
                        ))
                    }
                </div>
                <button type="submit" className="seller-save-btn" disabled={isSaving}>
                    {isSaving ? 'Saving…' : 'Save Categories'}
                </button>
            </form>
            {message && (
                <p className={`seller-message${message.includes('Failed') ? ' seller-message--error' : ''}`}>
                    {message}
                </p>
            )}
        </div>
    );
};

// ── PendingItem ───────────────────────────────────────────────────────────────
const PendingItem = ({ interaction, onComplete }) => {
    const [confirming, setConfirming] = useState(false);
    const [rating,     setRating]     = useState(5);
    const [submitting, setSubmitting] = useState(false);
    const [itemError,  setItemError]  = useState('');

    const handleConfirm = async (e) => {
        e.preventDefault();
        if (!rating) { setItemError('Please select a rating.'); return; }
        setSubmitting(true);
        setItemError('');
        try {
            const res = await api.put(`/posts/${interaction.postId}/complete`, {
                interactionId: interaction._id,
                role: 'responder',
                rating,
            });
            onComplete(interaction._id, res.data);
        } catch (err) {
            setItemError(err.response?.data?.message || 'Failed to confirm — please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="pending-item">
            <div className="pending-item-body">
                <div className="pending-item-meta">
                    <span className={`badge badge--${interaction.type}`}>
                        {interaction.type.charAt(0).toUpperCase() + interaction.type.slice(1)}
                    </span>
                    {interaction.price != null && (
                        <span className="tx-price">${interaction.price.toFixed(2)}</span>
                    )}
                </div>
                <p className="pending-post-content">
                    {truncate(interaction.postContent, 120)}
                </p>
                <p className="pending-requester">
                    From <strong>{interaction.requesterName || 'Anonymous'}</strong>
                </p>
            </div>

            {!confirming ? (
                <button className="confirm-open-btn" onClick={() => setConfirming(true)}>
                    Confirm Completion
                </button>
            ) : (
                <form className="confirm-form" onSubmit={handleConfirm}>
                    <label className="confirm-form-label">Your rating (required)</label>
                    <StarRating value={rating} onChange={setRating} disabled={submitting} />
                    {itemError && (
                        <span className="item-error" role="alert">{itemError}</span>
                    )}
                    <div className="confirm-form-actions">
                        <button type="submit" className="confirm-submit-btn" disabled={submitting}>
                            {submitting ? 'Submitting…' : 'Submit'}
                        </button>
                        <button
                            type="button"
                            className="confirm-cancel-btn"
                            disabled={submitting}
                            onClick={() => { setConfirming(false); setItemError(''); setRating(5); }}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

// ── Profile ───────────────────────────────────────────────────────────────────
const Profile = () => {
    const [profileData, setProfileData] = useState(null);
    const [loading,     setLoading]     = useState(true);
    const [error,       setError]       = useState(null);
    const [activeTab,   setActiveTab]   = useState('overview');

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await api.get('/users/profile');
                setProfileData(res.data);
            } catch (err) {
                console.error('Error fetching profile:', err);
                setError('Failed to load profile — please try again.');
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, []);

    const handleCategoriesUpdate = (newCategories) => {
        setProfileData(prev => ({
            ...prev,
            user: { ...prev.user, seller_categories: newCategories },
        }));
    };

    const handleStatusUpdate = (newStatus) => {
        setProfileData(prev => ({
            ...prev,
            user: {
                ...prev.user,
                is_shopkeeper: newStatus,
                ...(newStatus === false && { seller_categories: [] }),
            },
        }));
    };

    // Kept verbatim — handles partial/full completion and post deletion
    const handleTransactionComplete = (interactionId, responseData) => {
        const updatedInteractionDetails = responseData.interaction;
        const postWasDeleted = responseData.postDeleted === true;

        setProfileData(prevData => {
            let postIdToRemove = null;

            const inReceived = prevData.interactionsReceived.find(i => i._id === interactionId);
            if (inReceived) {
                postIdToRemove = inReceived.postId;
            } else {
                const inPending = prevData.pendingResponderConfirmations.find(i => i._id === interactionId);
                if (inPending) postIdToRemove = inPending.postId;
            }

            const updateList = (list) =>
                list.map(i => i._id === interactionId ? { ...i, ...updatedInteractionDetails } : i);

            let newInteractionsReceived         = updateList(prevData.interactionsReceived);
            let newPendingResponderConfirmations = updateList(prevData.pendingResponderConfirmations);

            if (postWasDeleted && postIdToRemove) {
                const newPosts = prevData.posts.filter(p => p._id !== postIdToRemove);
                newInteractionsReceived         = newInteractionsReceived.filter(i => i._id !== interactionId);
                newPendingResponderConfirmations = newPendingResponderConfirmations.filter(i => i._id !== interactionId);
                return {
                    ...prevData,
                    posts: newPosts,
                    interactionsReceived: newInteractionsReceived,
                    pendingResponderConfirmations: newPendingResponderConfirmations,
                };
            }

            return {
                ...prevData,
                interactionsReceived: newInteractionsReceived,
                pendingResponderConfirmations: newPendingResponderConfirmations,
            };
        });
    };

    const findPostId = (interactionId) => {
        const inReceived = profileData.interactionsReceived.find(i => i._id === interactionId);
        if (inReceived) return inReceived.postId;
        const inPending = profileData.pendingResponderConfirmations.find(i => i._id === interactionId);
        return inPending?.postId ?? null;
    };

    // ── Loading / error ───────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="profile-skeleton">
                <div className="pskel-head" />
                <div className="pskel-tabs" />
                <div className="pskel-body" />
            </div>
        );
    }
    if (error)              return <div className="profile-fetch-error" role="alert">{error}</div>;
    if (!profileData?.user) return <div className="profile-fetch-error">No profile data found.</div>;

    const {
        user, posts, interactions, interactionsReceived,
        pendingResponderConfirmations, statistics,
    } = profileData;

    const pendingCount  = pendingResponderConfirmations.length;
    const avgRating     = user.average_rating || 0;
    const ratingDisplay = avgRating > 0 ? avgRating.toFixed(1) : null;

    const TABS = [
        { key: 'overview',     label: 'Overview' },
        { key: 'posts',        label: 'My Posts' },
        { key: 'transactions', label: 'Transactions' },
        { key: 'pending',      label: 'Pending', count: pendingCount },
    ];

    return (
        <div className="profile">

            {/* ── Page header ── */}
            <div className="profile-head">
                <div className="profile-avatar-lg" aria-hidden="true">
                    {(user.name?.[0] || '?').toUpperCase()}
                </div>
                <div className="profile-info">
                    <span className="profile-name">{user.name || 'Anonymous'}</span>
                    <span className="profile-email">{user.email}</span>
                    <div className="profile-rep">
                        <StarRating value={Math.round(avgRating)} />
                        <span className="rep-label">
                            {ratingDisplay ? `${ratingDisplay} / 5` : 'No rating yet'}
                            {' · '}
                            {user.completed_transactions_count ?? 0} completed
                        </span>
                    </div>
                </div>
            </div>

            {/* ── Tab bar ── */}
            <div className="tab-bar" role="tablist">
                {TABS.map(tab => (
                    <button
                        key={tab.key}
                        role="tab"
                        aria-selected={activeTab === tab.key}
                        className={`tab-btn${activeTab === tab.key ? ' tab-btn--active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                        {tab.count > 0 && (
                            <span className="tab-badge">{tab.count}</span>
                        )}
                    </button>
                ))}
            </div>

            {/* ── Tab content ── */}
            <div className="tab-content" role="tabpanel">

                {/* Overview ────────────────────────────────────────────────── */}
                {activeTab === 'overview' && (
                    <div className="tab-pane">
                        {statistics && (
                            <div className="stat-cards">
                                <StatCard label="Total Posts"           value={statistics.totalPosts} />
                                <StatCard label="Interactions Made"     value={statistics.totalInteractions} />
                                <StatCard label="Interactions Received" value={statistics.totalInteractionsReceived} />
                                <StatCard
                                    label="Average Rating"
                                    value={ratingDisplay ? `${ratingDisplay} / 5` : '—'}
                                />
                            </div>
                        )}
                        <div className="overview-extra">
                            <ShopkeeperStatusToggle
                                isShopkeeper={user.is_shopkeeper || false}
                                onStatusChange={handleStatusUpdate}
                            />
                            {user.is_shopkeeper && (
                                <div className="seller-section">
                                    <p className="specialties-label">
                                        <strong>Current specialties: </strong>
                                        {user.seller_categories?.length > 0
                                            ? user.seller_categories.join(', ')
                                            : 'None set — add them below for a prioritised feed.'}
                                    </p>
                                    <SellerCategoriesEditor
                                        initialCategories={user.seller_categories || []}
                                        onUpdate={handleCategoriesUpdate}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* My Posts ───────────────────────────────────────────────── */}
                {activeTab === 'posts' && (
                    <div className="tab-pane">
                        {posts.length === 0 ? (
                            <div className="tab-empty">
                                <span className="tab-empty-icon">🏘️</span>
                                <p>You haven't shared any posts yet.</p>
                            </div>
                        ) : (
                            <div className="post-list">
                                {posts.map(post => {
                                    const ai       = post.ai_categorization;
                                    const postType = ai?.post_type_suggestion;
                                    const category = ai?.category;
                                    return (
                                        <div key={post._id} className="post-list-item">
                                            <p className="post-list-content">
                                                {truncate(post.content, 120)}
                                            </p>
                                            <div className="post-list-meta">
                                                {postType && postType !== 'General' && (
                                                    <span className={`badge badge--${postType.toLowerCase()}`}>
                                                        {postType}
                                                    </span>
                                                )}
                                                {category && category !== 'General' && (
                                                    <span className="badge badge--category">
                                                        {category}
                                                    </span>
                                                )}
                                                <span className="radius-pill">{post.radius}m radius</span>
                                                <span className="post-list-date">
                                                    {new Date(post.createdAt).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Transactions ───────────────────────────────────────────── */}
                {activeTab === 'transactions' && (
                    <div className="tab-pane">
                        <p className="tx-section-title">Interactions I Made</p>
                        {interactions.length === 0 ? (
                            <p className="tx-empty">No interactions made yet.</p>
                        ) : (
                            <div className="tx-list">
                                {interactions.map(ix => (
                                    <div key={ix._id} className="tx-item">
                                        <div className="tx-item-top">
                                            <span className={`badge badge--${ix.type}`}>
                                                {ix.type.charAt(0).toUpperCase() + ix.type.slice(1)}
                                            </span>
                                            <span className={`status-pill${ix.is_completed ? ' status-pill--done' : ' status-pill--pending'}`}>
                                                {ix.is_completed ? 'Completed' : 'Pending'}
                                            </span>
                                            {ix.price != null && (
                                                <span className="tx-price">${ix.price.toFixed(2)}</span>
                                            )}
                                            <span className="tx-date">
                                                {new Date(ix.createdAt).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <p className="tx-post-content">
                                            {truncate(ix.postContent, 100)}
                                        </p>
                                        {ix.responderName && (
                                            <p className="tx-party">
                                                Responder: <strong>{ix.responderName}</strong>
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        <p className="tx-section-title tx-section-title--spaced">
                            Interactions I Received
                        </p>
                        {interactionsReceived.length === 0 ? (
                            <p className="tx-empty">No interactions received yet.</p>
                        ) : (
                            <div className="tx-list">
                                {interactionsReceived.map(ix => {
                                    const postId = findPostId(ix._id);
                                    return (
                                        <div key={ix._id} className="tx-item">
                                            <div className="tx-item-top">
                                                <span className={`badge badge--${ix.type}`}>
                                                    {ix.type.charAt(0).toUpperCase() + ix.type.slice(1)}
                                                </span>
                                                <span className={`status-pill${ix.is_completed ? ' status-pill--done' : ' status-pill--pending'}`}>
                                                    {ix.is_completed ? 'Completed' : 'Pending'}
                                                </span>
                                                {ix.price != null && (
                                                    <span className="tx-price">${ix.price.toFixed(2)}</span>
                                                )}
                                                <span className="tx-date">
                                                    {new Date(ix.createdAt).toLocaleDateString()}
                                                </span>
                                            </div>
                                            <p className="tx-post-content">
                                                {truncate(ix.postContent, 100)}
                                            </p>
                                            {ix.responderName && (
                                                <p className="tx-party">
                                                    From: <strong>{ix.responderName}</strong>
                                                </p>
                                            )}
                                            {!ix.is_completed && postId && (
                                                <InteractionCompletionManager
                                                    interaction={ix}
                                                    postId={postId}
                                                    isAuthor={true}
                                                    isResponder={false}
                                                    currentUserId={user._id}
                                                    onComplete={handleTransactionComplete}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Pending ────────────────────────────────────────────────── */}
                {activeTab === 'pending' && (
                    <div className="tab-pane">
                        {pendingCount === 0 ? (
                            <div className="tab-empty">
                                <span className="tab-empty-icon">✅</span>
                                <p>No transactions waiting for your confirmation.</p>
                            </div>
                        ) : (
                            <div className="pending-list">
                                {pendingResponderConfirmations.map(interaction => (
                                    <PendingItem
                                        key={interaction._id}
                                        interaction={interaction}
                                        onComplete={handleTransactionComplete}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}

            </div>
        </div>
    );
};

export default Profile;
